import { Link } from 'src/domains/layouts/webview/components/router/NewLink';
import styled from '@emotion/styled';
import { withConfig } from 'src/withConfig';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { css, SerializedStyles } from '@emotion/react';

const setEventRowWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme, isOutrights: boolean, layoutOptions?: Record<string, boolean>): SerializedStyles => {
    const { isColumnSelectionLayout } = layoutOptions || {};
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.medium};
            padding: ${isOutrights === true ? '0': theme.star.padding.small};
            background-color: ${isOutrights === true ? 'transparent': theme.star.eventRow.bgColor};
            gap: ${theme.star.gap.medium};
            min-height: 64px;
        `;
    }
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            padding: ${theme.star.padding.medium};
            background-color: inherit;
            gap: ${theme.star.gap.small};
            min-height: 64px;
            border-bottom: solid 1px ${theme.star.unified.eventRow.borderColor};

            ${isColumnSelectionLayout && css`
                min-height: 60px;
                border-bottom: none;

                & [class$='RowLinkLabelWrapper'] {
                    gap: ${theme.star.gap.small};
                }
            `}

            ${isOutrights === true && css`
                padding: 0;
                background-color: transparent;
                gap: 0;
                border-bottom: none;
                min-height: unset;
            `}
        `;
    }
    return css`
        [class$='CollapseWrapper'] + &,
        [class$='EventsListGroupWrapper'] > &:first-of-type {
            border-top: solid 1px ${theme.star.eventRow.borderColor};
        }
        background-color: ${theme.star.eventRow.bgColor};
        border-bottom: solid 1px ${theme.star.eventRow.borderColor};
        border-left: solid 1px ${theme.star.eventRow.borderColor};
        min-height: 50px;
    `;
};

interface EventRowPropsType {
    variant: DefaultComponentVariantType | null;
    isOutrights: boolean;
    layoutOptions?: Record<string, boolean>
}

export const EventRowWrapper = withConfig(theme => styled('div', { label: 'EventRowWrapper' })<EventRowPropsType>`
    ${({ variant, isOutrights, layoutOptions }): SerializedStyles => setEventRowWrapper(variant, theme, isOutrights, layoutOptions)};
    display: flex;
    flex-flow: ${({ variant, isOutrights, layoutOptions }): string => {
        if (layoutOptions?.isColumnSelectionLayout === true) {
            return 'column nowrap';
        }

        return variant === DefaultComponentVariantType.TERTIARY && isOutrights === false ? 'row nowrap' : 'row wrap';
    }};
    justify-content: space-between;
    position: relative;
    z-index: ${theme.star.zIndexGlobal.base};

    & [class$='TableSportsRowLinkWrapper'] {
        padding: ${theme.star.padding.medium};
        border: 0;
        border-bottom: 1px solid ${theme.star.unified.eventRow.borderColor};
        flex-flow: row;
        align-items: center;

        & [class$='RowLinkLabel'] {
            font-size: ${theme.star.unified.tableSportsRow.link.typography.fontSizePrimary};
            font-weight: ${theme.star.unified.tableSportsRow.link.typography.fontWeightPrimary};
            line-height: ${theme.star.unified.tableSportsRow.link.typography.lineHeightPrimary};
            display: flex;
            align-items: center;
            min-height: 44px;
        }
    }
`);

interface EventRowNameLinkPropsType {
    variant: DefaultComponentVariantType | null;
    layoutOptions?: Record<string, boolean>;
}

const setEventRowNameLink = (
    variant: DefaultComponentVariantType | null,
    theme: EmotionTheme,
    layoutOptions?: Record<string, boolean>
): SerializedStyles => {
    const { isColumnSelectionLayout } = layoutOptions || {};

    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            padding: ${theme.star.padding.small} ${theme.star.padding.xMedium};
        `;
    }
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            ${theme.star.unified.eventRow.txtColor};
            padding: 0 ${theme.star.padding.small};
            gap: ${theme.star.gap.small};
            ${isColumnSelectionLayout === true
                ? `
                      flex-flow: row;
                      align-items: center;
                  `
                : ''}
        `;
    }
    return css`
        padding: ${theme.star.padding.medium} ${theme.star.padding.xMedium};
        gap: ${theme.star.eventRow.gap};
    `;
};

export const EventRowNameLink = withConfig(theme => styled(Link, { label: 'EventRowNameLink' })<EventRowNameLinkPropsType>`
    color: ${theme.star.eventRow.txtColor};
    align-items: flex-start;
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    ${({ variant, layoutOptions }): SerializedStyles => setEventRowNameLink(variant, theme, layoutOptions)};
    overflow: hidden;
    text-decoration: none;
    text-overflow: ellipsis;
    width: 100%;
`);

interface EventRowHeaderPropsType {
    variant: DefaultComponentVariantType | null;
}

const setEventRowHeader = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            background: ${theme.star.eventRow.bgColor};
            border-radius: ${theme.star.radius.medium};
        `;
    }
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            background: ${theme.star.eventRow.bgColor};
            border-radius: ${theme.star.radius.medium};
        `;
    }
    return css``;
};

export const EventRowHeader = withConfig(theme => styled('div', { label: 'EventRowHeader' })<EventRowHeaderPropsType>`
    ${({ variant }): SerializedStyles => setEventRowHeader(variant, theme)}
    display: flex;
    flex: 1 1 0%;
    flex-flow: row nowrap;
    line-height: 1;
    min-width: 50px;
    transition: background-color .2s ease;
`);

export const EventRowMarkets = styled('div', { label: 'EventRowMarkets' })<EventRowPropsType>`
    min-width: 100%;
    margin-top: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? '4px' : variant === DefaultComponentVariantType.TERTIARY ? '4px' : '0'};
`;

export const EventRowSelections = withConfig(theme => styled('ul', { label: 'EventRowSelections' })<EventRowPropsType>`
    display: flex;
    flex-flow: row nowrap;
    font-weight: ${theme.star.fontWeight.bold};
    justify-content: center;
    gap: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.gap.small : variant === DefaultComponentVariantType.TERTIARY ? theme.star.gap.small : '0'};
`);

export const EventRowSelectionsElement = styled('li', { label: 'EventRowSelectionsElement' })`
    display: flex;
    height: 100%;
`;

const setEventRowMeta = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            color: ${theme.star.unified.eventRow.txtColorQuaternary};
            font-size: ${theme.star.unified.eventRow.typography.fontSizeSecondary};
            line-height: ${theme.star.unified.eventRow.typography.lineHeightSecondary};
        `;
    }
    return css`
        color: ${theme.star.eventRow.txtColorQuaternary};
        font-size: ${theme.star.eventRow.typography.fontSizeSecondary};
        line-height: ${theme.star.eventRow.typography.lineHeightSecondary};
    `;
};

export const EventRowMeta = withConfig(theme => styled('div', { label: 'EventRowMeta' })<{variant?: DefaultComponentVariantType | null}>`
    display: flex;
    white-space: nowrap;
    width: 100%;
    ${({ variant = null }): SerializedStyles => setEventRowMeta(variant, theme)}
`);
